// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// constant imports
import { CacheDuration } from '../../_constants/cache-duration';
import { URLConstants } from '../../_constants/url-constants';

// Service import
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// model imports
import { FileInfoDisplayResultDto } from '../../_models/file-info-display-result-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { BoardApprovalAccEditorResultDto } from '../_models/board-approval-acc-editor-result-dto';
import { BoardApprovalActionParameterDto } from '../_models/board-approval-action-parameter-dto';
import { BoardApprovalAddNotesParameterDto } from '../_models/board-approval-add-notes-parameter-dto';
import { BoardApprovalBaseParameterDto } from '../_models/board-approval-base-parameter-dto';
import { BoardApprovalCollectionReferralResultDto } from '../_models/board-approval-collection-referral-result-dto';
import { BoardApprovalCollectionsEditorResultDto } from '../_models/board-approval-collections-editor-result-dto';
import { BoardApprovalDetailParameterDto } from '../_models/board-approval-detail-parameter-dto';
import { BoardApprovalEditorParameterDto } from '../_models/board-approval-editor-parameter-dto';
import { BoardApprovalNotesEditorParameterDto } from '../_models/board-approval-notes-editor-parameter-dto';
import { BoardApprovalRulesResultDto } from '../_models/board-approval-rules-result-dto';
import { BoardApprovalWfEditorResultDto } from '../_models/board-approval-wf-editor-result-dto';
import { BoardApprovalWFParameterDto } from '../_models/board-approval-wf-parameter-dto';
import { CollectionsBoardApprovalDocumentParameterDto } from '../_models/collections-board-approval-document-parameter-dto';
import { CollectionsBoardApprovalDocumentRawDto } from '../_models/collections-board-approval-document-raw-dto';
import { CollectionsBoardApprovalParameterDto } from '../_models/collections-board-approval-parameter-dto';

@Injectable({
    providedIn: 'root'
})
export class SharedBoardApprovalService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    /* services for boardapproval/acc starts here */

    getBoardApprovalACCEditor(
        baseParams: BoardApprovalEditorParameterDto
    ): Observable<BoardApprovalAccEditorResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/editor${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(BoardApprovalAccEditorResultDto, response)));
    }

    approveBoardApproval(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/action/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }

    denyBoardApproval(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/action/deny`;
        return this.httpClient.post(url, classToPlain(params));
    }

    holdBoardApproval(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/action/hold`;
        return this.httpClient.post(url, classToPlain(params));
    }

    approveWithStipulationBoardApproval(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/action/approve-with-stipulation`;
        return this.httpClient.post(url, classToPlain(params));
    }

    getNotesForAcc(baseParams: BoardApprovalNotesEditorParameterDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/notes${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(BaseProcessNotesResultDto, response)));
    }

    saveNotesForAcc(params: BoardApprovalAddNotesParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/notes`;
        return this.httpClient.post(url, classToPlain(params));
    }

    getAccBoardRules(baseParams: BoardApprovalBaseParameterDto): Observable<BoardApprovalRulesResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/board-rules${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(BoardApprovalRulesResultDto, response)));
    }

    /* services for boardapproval/acc ends here */

    /* services for boardapproval/collections starts here */

    getBoardApprovalCollectionsEditor(
        baseParams: BoardApprovalEditorParameterDto
    ): Observable<BoardApprovalCollectionsEditorResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/editor${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(BoardApprovalCollectionsEditorResultDto, response)));
    }

    approveBoardApprovalCollections(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/action/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }

    denyBoardApprovalCollections(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/action/deny`;
        return this.httpClient.post(url, classToPlain(params));
    }

    holdBoardApprovalCollections(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/action/hold`;
        return this.httpClient.post(url, classToPlain(params));
    }

    approveWithStipulationBoardApprovalCollections(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/action/approve-with-stipulation`;
        return this.httpClient.post(url, classToPlain(params));
    }

    getNotesForCollections(baseParams: BoardApprovalNotesEditorParameterDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/notes${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(BaseProcessNotesResultDto, response)));
    }

    saveNotesForCollections(params: BoardApprovalAddNotesParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/notes`;
        return this.httpClient.post(url, classToPlain(params));
    }

    getCollectionsDetail(
        baseParams: BoardApprovalDetailParameterDto
    ): Observable<BoardApprovalCollectionReferralResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/detail${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(BoardApprovalCollectionReferralResultDto, response)));
    }

    getCollectionsBoardRules(baseParams: BoardApprovalBaseParameterDto): Observable<BoardApprovalRulesResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/board-rules${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(BoardApprovalRulesResultDto, response)));
    }

    downloadCollectionStatement(baseParams: BoardApprovalDetailParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/statement${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getCollectionDocuments(baseParams: CollectionsBoardApprovalParameterDto): Observable<FileInfoDisplayResultDto[]> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/documents${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(FileInfoDisplayResultDto, response)));
    }

    downloadCollectionDocument(baseParams: CollectionsBoardApprovalDocumentParameterDto): Observable<Blob> {
        const url = `${
            URLConstants.ccBoardApprovalConfigApiUrl
        }/collections/document/download${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    uploadCollectionDocument(baseParams: CollectionsBoardApprovalDocumentRawDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/document`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    deleteCollectionDocument(baseParams: CollectionsBoardApprovalDocumentParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/document`;

        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(baseParams)
        };
        return this.httpClient.delete(url, options);
    }

    /* services for boardapproval/collections ends here */

    /* services for boardapproval/wf starts here */

    getBoardApprovalWorkflowEditor(
        baseParams: BoardApprovalEditorParameterDto
    ): Observable<BoardApprovalWfEditorResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/editor${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(BoardApprovalWfEditorResultDto, response)));
    }

    approveBoardApprovalWf(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/action/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }

    denyBoardApprovalWf(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/action/deny`;
        return this.httpClient.post(url, classToPlain(params));
    }

    holdBoardApprovalWf(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/action/hold`;
        return this.httpClient.post(url, classToPlain(params));
    }

    approveWithStipulationBoardApprovalWf(params: BoardApprovalActionParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/action/approve-with-stipulation`;
        return this.httpClient.post(url, classToPlain(params));
    }

    getWfCheckImage(baseParams: BoardApprovalWFParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/checkimage${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getNotesForWorkflow(baseParams: BoardApprovalNotesEditorParameterDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/notes${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(BaseProcessNotesResultDto, response)));
    }

    saveNotesForWorkflow(params: BoardApprovalAddNotesParameterDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/notes`;
        return this.httpClient.post(url, classToPlain(params));
    }

    getWorkflowDocument(baseParams: BoardApprovalWFParameterDto): Observable<FileInfoDisplayResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/document${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(FileInfoDisplayResultDto, response)));
    }

    getWFBoardApprovalRules(baseParams: BoardApprovalBaseParameterDto): Observable<BoardApprovalRulesResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/board-rules${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(BoardApprovalRulesResultDto, response)));
    }

    /* services for boardapproval/wf ends here */
}
